import { T } from "../adminTheme"
import { Fact, Facts, Muted, StatusBadge } from "./adminUi"

export interface ICardholderFlow {
	stage?: string
	label?: string
	description?: string
	actor?: "buyer" | "admin" | "system" | "issuer" | "none"
	ready_for_cards?: boolean
	next_action?: string
	next_endpoint?: string | null
	blocking?: string | null
	missing_for_issuer?: string[] | null
	missing_evaluated?: boolean
}

const STAGES: Record<string, string> = {
	approved: "Ready for cards",
	issuer_registration_required: "Not registered with issuer",
	ready_to_submit: "Ready to submit",
	data_required: "Missing holder data",
	phone_required: "Phone required",
	kyc_in_progress: "KYC not completed",
	kyc_under_review: "KYC under review",
	kyc_declined: "KYC declined",
	kyc_expired: "KYC session expired",
	issuer_review: "Issuer audit in progress",
	rejected: "Rejected by issuer",
	error: "Issuer request failed",
}

const historicalKyc = (flow: ICardholderFlow, ourKycStatus?: string) => ourKycStatus === "approved" && ["kyc_declined", "kyc_expired"].includes(flow.stage ?? "")

export const stageLabel = (flow: ICardholderFlow) =>
	flow.label || (flow.stage === "approved" && flow.ready_for_cards !== true ? "Card readiness not confirmed" : STAGES[flow.stage ?? ""] || flow.stage || "Unknown stage")

export const holderAction = (flow?: ICardholderFlow, ourKycStatus?: string) => {
	if (!flow || flow.ready_for_cards || flow.stage === "approved") return null
	if (historicalKyc(flow, ourKycStatus)) return null
	if (flow.actor === "system" || flow.actor === "issuer") return "Refresh status"
	if (flow.actor === "none") return null
	if (flow.stage === "issuer_review" || flow.stage === "kyc_under_review") return "Refresh status"
	if (!flow.stage || !STAGES[flow.stage]) return null
	return flow.stage === "phone_required" ? "Complete phone details"
		: ["issuer_registration_required", "ready_to_submit", "error"].includes(flow.stage) ? "Prepare issuer submission"
			: "Complete manual KYC"
}

const Line = ({ children, color }: { children: React.ReactNode; color?: string }) => <Muted style={{ display: "block", fontSize: 12.5, lineHeight: 1.45, ...(color ? { color } : {}) }}>{children}</Muted>

export const CardholderFlow = ({ flow, ourKycStatus }: { flow?: ICardholderFlow; ourKycStatus?: string }) => {
	if (!flow) return <Muted style={{ fontSize: 12.5 }}>Flow unavailable</Muted>
	const historical = historicalKyc(flow, ourKycStatus)
	const neutral = historical || ["ready_to_submit", "issuer_registration_required"].includes(flow.stage ?? "")
	const [color, background, line] = neutral ? [T.muted, T.hover, T.border] : flow.ready_for_cards === true ? [T.success, T.successSoft, T.successLine] : [T.warn, T.warnSoft, T.warnLine]
	return <div style={{ display: "grid", gap: 4, minWidth: 0, overflowWrap: "anywhere" }}>
		<span title={flow.description || flow.stage} style={{ display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content", padding: "1px 9px", borderRadius: 999, fontSize: 12.5, fontWeight: 550, color, background, boxShadow: `inset 0 0 0 1px ${line}` }}><i aria-hidden style={{ width: 6, height: 6, borderRadius: 6, background: color, flex: "none" }} />{stageLabel(flow)}</span>
		{flow.description && <Line>{flow.description}</Line>}
		{flow.actor && !historical && <Line>{({ buyer: "Action by: user", admin: "Action by: admin", system: "Handled automatically", issuer: "Waiting for issuer", none: "No action needed" })[flow.actor]}</Line>}
		{historical && <Line>Earlier holder KYC attempt; user KYC is approved. This does not block the user.</Line>}
		{flow.next_action && !historical && <Line>{flow.next_action}</Line>}
		{flow.blocking && !historical && <Line color={T.warn}>Blocking: {flow.blocking}</Line>}
		{!historical && (flow.missing_evaluated !== true
			? <Line color={T.warn}>Missing fields not evaluated</Line>
			: Boolean(flow.missing_for_issuer?.length) && <Line color={T.warn}>Missing: {flow.missing_for_issuer!.map((path) => path.split(".").pop()!.replaceAll("_", " ")).join(", ")}</Line>)}
	</div>
}

export const CardholderStatuses = ({ holder }: { holder: { status?: string; kyc_status?: string; user_kyc_status?: string; user_status?: string; user_company_status?: string } }) => <Facts>
	<Fact label="Issuer audit"><StatusBadge value={holder.status || "unknown"} /></Fact>
	<Fact label="Issuer KYC"><StatusBadge value={holder.kyc_status || "unknown"} /></Fact>
	<Fact label="Our user KYC">{holder.user_kyc_status === "pending" ? "Pending — may not have been started" : <StatusBadge value={holder.user_kyc_status || "unknown"} />}</Fact>
	<Fact label="User"><StatusBadge value={holder.user_status || "unknown"} /></Fact>
	<Fact label="Company access"><StatusBadge value={holder.user_company_status || "unknown"} /></Fact>
</Facts>
